import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, MapPin, Clock, ExternalLink, Ticket, Filter, Users, Music } from 'lucide-react';
import './EventsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventsPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    region: '',
    upcoming_only: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // list or calendar

  useEffect(() => {
    fetchEvents();
  }, [filters]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      
      const response = await axios.get(`${API}/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getCategoryIcon = (category) => {
    if (category === 'highland_games') return '🏴';
    if (category === 'military_tattoo') return '🥁';
    return '🎉';
  };

  const getCategoryName = (category) => {
    if (category === 'highland_games') return 'Highland Games';
    if (category === 'military_tattoo') return 'Military Tattoo';
    return 'Event';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Lade Veranstaltungen...</p>
      </div>
    );
  }

  return (
    <div className="events-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">
            <Calendar size={32} />
            Veranstaltungen
          </h1>
          <p className="page-subtitle">Highland Games & Military Tattoo Events in Schottland</p>
        </div>
        <div className="header-actions">
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={20} />
            Filter {showFilters ? 'verbergen' : 'anzeigen'}
          </button>
        </div>
      </header>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Kategorie</label>
              <select 
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <option value="">Alle</option>
                <option value="highland_games">Highland Games</option>
                <option value="military_tattoo">Military Tattoo</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Region</label>
              <select 
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
              >
                <option value="">Alle</option>
                <option value="Highlands">Highlands</option>
                <option value="Islands">Islands</option>
                <option value="Borders">Borders</option>
                <option value="Cities">Cities</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                <input 
                  type="checkbox"
                  checked={filters.upcoming_only}
                  onChange={(e) => setFilters({...filters, upcoming_only: e.target.checked})}
                />
                Nur zukünftige Events
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="events-count">
        {events.length} {events.length === 1 ? 'Veranstaltung' : 'Veranstaltungen'} gefunden
      </div>

      {events.length === 0 ? (
        <div className="no-events">
          <Calendar size={64} />
          <p>Keine Veranstaltungen gefunden</p>
        </div>
      ) : (
        <div className="events-grid">
          {events.map((event) => (
            <div key={event.id} className="event-card">
              <div className="event-header">
                <span className="event-category-badge">
                  {getCategoryIcon(event.category)}
                  {getCategoryName(event.category)}
                </span>
                <span className="event-region">{event.region}</span>
              </div>

              <h3 className="event-title">{event.title}</h3>
              <p className="event-description">{event.description}</p>

              <div className="event-details">
                <div className="event-detail">
                  <Calendar size={18} />
                  <span>
                    {formatDate(event.date_from)}
                    {event.date_from !== event.date_to && ` - ${formatDate(event.date_to)}`}
                  </span>
                </div>

                {event.time_from && (
                  <div className="event-detail">
                    <Clock size={18} />
                    <span>{event.time_from} - {event.time_to}</span>
                  </div>
                )}

                <div className="event-detail">
                  <MapPin size={18} />
                  <span>{event.venue_name}, {event.region}</span>
                </div>

                {event.price_from && (
                  <div className="event-detail price">
                    <Ticket size={18} />
                    <span>ab {event.price_from} {event.price_currency}</span>
                  </div>
                )}
              </div>

              {event.category === 'highland_games' && event.disciplines.length > 0 && (
                <div className="event-highlights">
                  <strong>Disziplinen:</strong>
                  <div className="event-tags">
                    {event.disciplines.slice(0, 3).map((discipline, idx) => (
                      <span key={idx} className="event-tag">{discipline}</span>
                    ))}
                    {event.disciplines.length > 3 && (
                      <span className="event-tag">+{event.disciplines.length - 3} mehr</span>
                    )}
                  </div>
                </div>
              )}

              {event.category === 'highland_games' && (
                <div className="event-info-badges">
                  {event.pipe_bands && (
                    <span className="info-badge">
                      <Music size={14} />
                      {event.pipe_bands} Pipe Bands
                    </span>
                  )}
                  {event.clan_count && (
                    <span className="info-badge">
                      <Users size={14} />
                      {event.clan_count} Clans
                    </span>
                  )}
                </div>
              )}

              {event.category === 'military_tattoo' && (
                <div className="event-info-badges">
                  {event.duration_minutes && (
                    <span className="info-badge">
                      <Clock size={14} />
                      {event.duration_minutes} Min
                    </span>
                  )}
                  {event.fireworks && (
                    <span className="info-badge">
                      🎆 Feuerwerk
                    </span>
                  )}
                </div>
              )}

              <div className="event-actions">
                {event.ticket_link && (
                  <a 
                    href={event.ticket_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-ticket"
                  >
                    <Ticket size={18} />
                    Tickets
                  </a>
                )}
                {event.official_link && (
                  <a 
                    href={event.official_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-info"
                  >
                    <ExternalLink size={18} />
                    Info
                  </a>
                )}
              </div>

              <div className="event-footer">
                <span className="event-organizer">Veranstalter: {event.organizer}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
